import { API_BASE, refresh } from "modules/members/api";

//https://dev.to/dinkydani21/how-we-use-a-popup-for-google-and-outlook-oauth-oci
let windowObjectReference: Window | null;
let previousUrl: string;
const BASE_URL = window.location.origin;

/** The sign-in request path; `reauth` asks the IdP to make the person sign in again. */
export const signInRequestPath = (
  provider: string,
  subdomain: string,
  opts: { reauth?: boolean } = {},
) =>
  `auth/${provider}/request?subdomain=${encodeURIComponent(subdomain)}` +
  (opts.reauth ? "&reauth=1" : "");

const authUrl = (path: string) => API_BASE + "/" + path;

/** Sign in in this tab rather than a popup, which needs a click to be allowed. */
export const redirectToSignIn = (path: string) => {
  window.location.assign(authUrl(path));
};

// Stops the popup opened last from reporting back. Only one popup is ever
// listened to: a re-authentication retries whatever asked for it, so an older
// listener left behind would repeat an action the person has since moved on
// from (or cancelled) the next time any popup reports success.
let disposeActive: (() => void) | null = null;

/**
 * Opens the sign-in popup and listens for it to report success.
 *
 * `onToken` runs once the portal has refreshed its token onto the session the
 * popup created; `onError` runs if that refresh fails or finds the session
 * gone. Each popup answers at most once.
 *
 * Returns a disposer: after it is called neither callback runs, even for a
 * sign-in finished later in a popup that is still open. A caller that can be
 * cancelled or unmounted must call it.
 */
export const openSignInWindow = (
  path: string,
  name: string,
  onToken: (state: object) => void,
  onError?: (error: unknown) => void,
): (() => void) => {
  const url = authUrl(path);
  disposeActive?.();

  let disposed = false;
  const listener = (event: MessageEvent) => {
    // Do we trust the sender of this message? (might be
    // different from what we originally opened, for example).
    if (disposed || event.origin !== BASE_URL) return;
    if (event.data?.source !== "member-auth") return;
    window.removeEventListener("message", listener);

    refresh()
      .then((result) => {
        if (disposed) return;
        if (result) onToken({});
        else onError?.(new Error("The session ended before sign-in finished."));
      })
      .catch((error) => {
        if (!disposed) onError?.(error);
      });
  };

  const dispose = () => {
    disposed = true;
    window.removeEventListener("message", listener);
    if (disposeActive === dispose) disposeActive = null;
  };
  disposeActive = dispose;

  // window features
  const strWindowFeatures =
    "toolbar=no, menubar=no, width=600, height=700, top=100, left=100";

  if (windowObjectReference === null || windowObjectReference?.closed) {
    /* if the pointer to the window object in memory does not exist
      or if such pointer exists but the window was closed */
    windowObjectReference = window.open(url, name, strWindowFeatures);
  } else if (previousUrl !== url) {
    /* if the resource to load is different,
      then we load it in the already opened secondary window and then
      we bring such window back on top/in front of its parent window. */
    windowObjectReference = window.open(url, name, strWindowFeatures);
    windowObjectReference?.focus();
  } else {
    /* else the window reference must exist and the window
      is not closed; therefore, we can bring it back on top of any other
      window with the focus() method. There would be no need to re-create
      the window or to reload the referenced resource. */
    windowObjectReference.focus();
  }

  // add the listener for receiving a message from the popup
  window.addEventListener("message", listener, false);
  // assign the previous URL
  previousUrl = url;

  return dispose;
};
